import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import TodayIcon from "@mui/icons-material/Today";
import {
  alpha,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Slide,
  ThemeProvider,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DetailedMatch, MatchStatus } from "@virtualprogaming/js-public-sdk";
import {
  addMonths,
  format,
  getDay,
  getDaysInMonth,
  isSameMonth,
  isToday,
  startOfMonth,
  subMonths,
} from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { groupBy, isEmpty, uniqBy } from "lodash-es";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { useUserMyTeams } from "../../../queries";
import { useTeamMatches } from "../../../queries/teams";
import {
  Boundary,
  CDNImage,
  ImageVariant,
  SafeStickyAd,
} from "../../components";
import { appTheme } from "../../theme";

const ENHANCED_CARD_STYLE = {
  background:
    "linear-gradient(145deg, rgba(26, 26, 26, 0.95) 0%, rgba(35, 35, 35, 0.95) 100%)",
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(appTheme.palette.primary.main, 0.15)}`,
  borderRadius: 4,
  transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${
      appTheme.palette.primary.main
    }, ${alpha(appTheme.palette.primary.main, 0.3)})`,
  },
  boxShadow: `0 4px 20px ${alpha("#000", 0.3)}`,
};

const SELECTOR_STYLE = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  py: 1,
  px: 2,
  borderRadius: 2,
  backgroundColor: alpha(appTheme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(appTheme.palette.primary.main, 0.2)}`,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(appTheme.palette.primary.main, 0.15),
    borderColor: alpha(appTheme.palette.primary.main, 0.3),
  },
};

const TEAM_LOGO_CONTAINER_STYLE = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  overflow: "hidden",
  border: `2px solid ${alpha(appTheme.palette.primary.main, 0.3)}`,
  boxShadow: `0 2px 8px ${alpha("#000", 0.3)}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: alpha("#000", 0.3),
  mr: 1.5,
};

const MENU_PAPER_PROPS_SX = {
  backgroundColor: alpha("#1A1A1A", 0.95),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(appTheme.palette.primary.main, 0.2)}`,
  boxShadow: `0 4px 20px ${alpha("#000", 0.5)}`,
};

const EMPTY_CELL_STYLE = {
  backgroundColor: alpha(appTheme.palette.background.paper, 0.05),
  p: 1,
  m: 0.5,
  opacity: 0.3,
  border: `1px solid ${alpha(appTheme.palette.divider, 0.1)}`,
  borderRadius: 2,
};

const SCROLLBAR_STYLE = {
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: alpha("#FFF", 0.3),
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: alpha(appTheme.palette.primary.main, 0.5),
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${alpha("#FFF", 0.3)} transparent`,
};

// Global timezone data with compact names for mobile UI constraints
const TIMEZONE_OPTIONS = [
  {
    id: "Europe/London",
    name: "UK",
    fullName: "United Kingdom (GMT/BST)",
    offset: "UTC+0/+1",
  },
  {
    id: "Europe/Berlin",
    name: "Germany",
    fullName: "Germany (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Paris",
    name: "France",
    fullName: "France (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Rome",
    name: "Italy",
    fullName: "Italy (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Madrid",
    name: "Spain",
    fullName: "Spain (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Amsterdam",
    name: "Netherlands",
    fullName: "Netherlands (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Stockholm",
    name: "Sweden",
    fullName: "Sweden (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Warsaw",
    name: "Poland",
    fullName: "Poland (CET/CEST)",
    offset: "UTC+1/+2",
  },
  {
    id: "Europe/Moscow",
    name: "Russia",
    fullName: "Russia (MSK)",
    offset: "UTC+3",
  },
  {
    id: "America/New_York",
    name: "US East",
    fullName: "US Eastern (EST/EDT)",
    offset: "UTC-5/-4",
  },
  {
    id: "America/Chicago",
    name: "US Central",
    fullName: "US Central (CST/CDT)",
    offset: "UTC-6/-5",
  },
  {
    id: "America/Denver",
    name: "US Mountain",
    fullName: "US Mountain (MST/MDT)",
    offset: "UTC-7/-6",
  },
  {
    id: "America/Los_Angeles",
    name: "US West",
    fullName: "US Pacific (PST/PDT)",
    offset: "UTC-8/-7",
  },
  {
    id: "America/Toronto",
    name: "Canada East",
    fullName: "Canada Eastern (EST/EDT)",
    offset: "UTC-5/-4",
  },
  {
    id: "America/Vancouver",
    name: "Canada West",
    fullName: "Canada Pacific (PST/PDT)",
    offset: "UTC-8/-7",
  },
  {
    id: "Australia/Sydney",
    name: "Australia E",
    fullName: "Australia Eastern (AEDT/AEST)",
    offset: "UTC+10/+11",
  },
  {
    id: "Australia/Perth",
    name: "Australia W",
    fullName: "Australia Western (AWST)",
    offset: "UTC+8",
  },
  { id: "Asia/Tokyo", name: "Japan", fullName: "Japan (JST)", offset: "UTC+9" },
  {
    id: "Asia/Seoul",
    name: "S. Korea",
    fullName: "South Korea (KST)",
    offset: "UTC+9",
  },
  {
    id: "Asia/Shanghai",
    name: "China",
    fullName: "China (CST)",
    offset: "UTC+8",
  },
  {
    id: "Asia/Singapore",
    name: "Singapore",
    fullName: "Singapore (SGT)",
    offset: "UTC+8",
  },
  { id: "Asia/Dubai", name: "UAE", fullName: "UAE (GST)", offset: "UTC+4" },
  {
    id: "Africa/Cairo",
    name: "Egypt",
    fullName: "Egypt (EET/EEST)",
    offset: "UTC+2/+3",
  },
  {
    id: "Africa/Johannesburg",
    name: "S. Africa",
    fullName: "South Africa (SAST)",
    offset: "UTC+2",
  },
  {
    id: "America/Sao_Paulo",
    name: "Brazil",
    fullName: "Brazil (BRT/BRST)",
    offset: "UTC-3/-2",
  },
  {
    id: "America/Mexico_City",
    name: "Mexico",
    fullName: "Mexico (CST/CDT)",
    offset: "UTC-6/-5",
  },
  {
    id: "Pacific/Auckland",
    name: "New Zealand",
    fullName: "New Zealand (NZDT/NZST)",
    offset: "UTC+12/+13",
  },
];

const getMatchStatusColor = (status: MatchStatus) => {
  switch (status) {
    case MatchStatus.Complete:
      return appTheme.palette.success.main;
    case MatchStatus.Scheduled:
      return appTheme.palette.info.main;
    case MatchStatus.InProgress:
      return appTheme.palette.warning.main;
    case MatchStatus.Disputed:
      return appTheme.palette.error.main;
    default:
      return appTheme.palette.grey[500];
  }
};

interface TeamInfo {
  name: string;
}

// Win/loss determination based on perspective of current team
const isWin = (match: DetailedMatch, currentTeam: TeamInfo): boolean => {
  if (
    match.status !== MatchStatus.Complete ||
    match.home_score === undefined ||
    match.away_score === undefined
  )
    return false;
  return match.home_name === currentTeam.name
    ? match.home_score > match.away_score
    : match.away_score > match.home_score;
};

const isDraw = (match: DetailedMatch): boolean => {
  if (
    match.status !== MatchStatus.Complete ||
    match.home_score === undefined ||
    match.away_score === undefined
  )
    return false;
  return match.home_score === match.away_score;
};

const isLoss = (match: DetailedMatch, currentTeam: TeamInfo): boolean => {
  if (
    match.status !== MatchStatus.Complete ||
    match.home_score === undefined ||
    match.away_score === undefined
  )
    return false;
  return match.home_name === currentTeam.name
    ? match.home_score < match.away_score
    : match.away_score < match.home_score;
};

const calculateMatchResults = (
  matches: DetailedMatch[],
  currentTeam: TeamInfo | undefined
) => {
  if (!currentTeam)
    return { wins: 0, draws: 0, losses: 0, points: 0, total: 0 };
  const wins = matches.filter((m) => isWin(m, currentTeam)).length;
  const draws = matches.filter(isDraw).length;
  const losses = matches.filter((m) => isLoss(m, currentTeam)).length;
  const points = wins * 3 + draws;
  return { wins, draws, losses, points, total: matches.length };
};

const getOpponentName = (
  match: DetailedMatch,
  currentTeam: TeamInfo | undefined
) => {
  if (!currentTeam) return match.home_name || match.away_name;
  return currentTeam.name === match.home_name
    ? match.away_name
    : match.home_name;
};

// Parallel API calls for all match statuses to avoid sequential loading delays
const useCombinedTeamMatches = (teamSlug: string | null) => {
  const queryOptions = { enabled: !!teamSlug };
  const { data: scheduled, isLoading: loadingScheduled } = useTeamMatches(
    teamSlug || "",
    MatchStatus.Scheduled,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    100,
    0,
    queryOptions
  );
  const { data: completed, isLoading: loadingCompleted } = useTeamMatches(
    teamSlug || "",
    MatchStatus.Complete,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    100,
    0,
    queryOptions
  );
  const { data: inProgress, isLoading: loadingInProgress } = useTeamMatches(
    teamSlug || "",
    MatchStatus.InProgress,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    100,
    0,
    queryOptions
  );
  const { data: disputed, isLoading: loadingDisputed } = useTeamMatches(
    teamSlug || "",
    MatchStatus.Disputed,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    100,
    0,
    queryOptions
  );

  const allMatches = useMemo(
    () => [
      ...(scheduled?.data || []),
      ...(completed?.data || []),
      ...(inProgress?.data || []),
      ...(disputed?.data || []),
    ],
    [scheduled, completed, inProgress, disputed]
  );

  const isLoading =
    loadingScheduled ||
    loadingCompleted ||
    loadingInProgress ||
    loadingDisputed;

  return { allMatches, isLoading };
};

const useCalendarNavigation = (initialDate = new Date()) => {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const goToPrevMonth = () => setCurrentDate((d) => subMonths(d, 1));
  const goToNextMonth = () => setCurrentDate((d) => addMonths(d, 1));
  const goToCurrentMonth = () => setCurrentDate(new Date());
  const prevMonthName = useMemo(
    () => format(subMonths(currentDate, 1), "MMMM"),
    [currentDate]
  );
  const nextMonthName = useMemo(
    () => format(addMonths(currentDate, 1), "MMMM"),
    [currentDate]
  );
  return {
    currentDate,
    goToPrevMonth,
    goToNextMonth,
    goToCurrentMonth,
    prevMonthName,
    nextMonthName,
  };
};

interface SelectorControlProps {
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  isOpen: boolean;
  ariaControls: string;
}
const SelectorControl: React.FC<SelectorControlProps> = ({
  children,
  onClick,
  isOpen,
  ariaControls,
}) => (
  <Box
    sx={SELECTOR_STYLE}
    onClick={onClick}
    aria-controls={ariaControls}
    aria-haspopup="true"
  >
    {children}
    <Box
      component="span"
      sx={{
        ml: 1,
        color: alpha(appTheme.palette.primary.main, 0.8),
        transition: "transform 0.3s ease",
        transform: isOpen ? "rotate(180deg)" : "rotate(0)",
        display: "flex",
      }}
    >
      ▼
    </Box>
  </Box>
);

interface StyledMenuProps {
  id: string;
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  minWidth?: number | string;
  maxHeight?: number | string;
}
const StyledMenu: React.FC<StyledMenuProps> = ({
  id,
  anchorEl,
  open,
  onClose,
  children,
  minWidth = 200,
  maxHeight,
}) => (
  <Menu
    id={id}
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    keepMounted
    PaperProps={{ sx: { ...MENU_PAPER_PROPS_SX, minWidth, maxHeight } }}
  >
    {children}
  </Menu>
);

interface StyledMenuItemProps {
  onClick: () => void;
  selected: boolean;
  children: React.ReactNode;
  selectedColor?: string;
}
const StyledMenuItem: React.FC<StyledMenuItemProps> = ({
  onClick,
  selected,
  children,
  selectedColor = appTheme.palette.primary.main,
}) => (
  <MenuItem
    onClick={onClick}
    selected={selected}
    sx={{
      py: 1.5,
      borderLeft: `3px solid ${selected ? selectedColor : "transparent"}`,
      backgroundColor: selected ? alpha(selectedColor, 0.1) : "transparent",
      "&:hover": { backgroundColor: alpha(selectedColor, 0.15) },
    }}
  >
    {children}
  </MenuItem>
);

const MatchStatusLegend: React.FC = () => {
  const legendItems = [
    { status: MatchStatus.Scheduled, label: "Upcoming" },
    { status: MatchStatus.InProgress, label: "In Progress" },
    { status: MatchStatus.Complete, label: "Completed" },
    { status: MatchStatus.Disputed, label: "Disputed" },
  ];
  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        flexWrap: "wrap",
        p: 1,
        borderRadius: 2,
        backgroundColor: alpha("#000", 0.2),
        border: `1px solid ${alpha(appTheme.palette.divider, 0.1)}`,
      }}
    >
      {legendItems.map((item) => (
        <Box
          key={item.status}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: getMatchStatusColor(item.status),
              borderRadius: "50%",
              boxShadow: `0 0 6px ${alpha(
                getMatchStatusColor(item.status),
                0.6
              )}`,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: alpha("#FFFFFF", 0.9),
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

interface DayResultBadgesProps {
  results: { wins: number; draws: number; losses: number; points: number };
}
const DayResultBadges: React.FC<DayResultBadgesProps> = ({
  results: { wins, draws, losses, points },
}) => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
    {wins > 0 && <Badge color={appTheme.palette.success.main}>{wins}W</Badge>}
    {draws > 0 && <Badge color={appTheme.palette.warning.main}>{draws}D</Badge>}
    {losses > 0 && <Badge color={appTheme.palette.error.main}>{losses}L</Badge>}
    <Badge
      color={appTheme.palette.primary.main}
      sx={{ ml: 0.25, px: 0.75, py: 0.15, fontSize: "0.7rem" }}
    >
      {points}pts
    </Badge>
  </Box>
);

const Badge: React.FC<{
  color: string;
  children: React.ReactNode;
  sx?: object;
}> = ({ color, children, sx }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      backgroundColor: alpha(color, 0.15),
      color,
      borderRadius: 1,
      px: 0.5,
      py: 0.1,
      ...sx,
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.6rem" }}>
      {children}
    </Typography>
  </Box>
);

interface MatchEntryProps {
  match: DetailedMatch;
  currentTeam: TeamInfo | undefined;
  onMatchClick: (match: DetailedMatch) => void;
  formatTimeInTimezone: (date: Date, formatStr: string) => string;
}
const DesktopMatchEntry: React.FC<MatchEntryProps> = ({
  match,
  currentTeam,
  onMatchClick,
  formatTimeInTimezone,
}) => (
  <Box
    onClick={() => onMatchClick(match)}
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      backgroundColor: alpha("#222", 0.6),
      borderRadius: 1,
      p: 0.75,
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderLeft: `3px solid ${getMatchStatusColor(match.status)}`,
      "&:hover": {
        backgroundColor: alpha("#333", 0.6),
        transform: "translateY(-1px)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <MatchTime
        datetime={match.datetime}
        formatTimeInTimezone={formatTimeInTimezone}
      />
      {match.status === MatchStatus.Complete && currentTeam && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            ml: 0.75,
            mr: 1,
            backgroundColor: alpha("#000", 0.3),
            borderRadius: "3px",
            px: 0.75,
            py: 0.25,
          }}
        >
          <MatchScore match={match} currentTeam={currentTeam} />
        </Box>
      )}
      <Tooltip
        title={getOpponentName(match, currentTeam) || ""}
        arrow
        placement="top"
        enterDelay={500}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            textOverflow: "ellipsis",
            fontSize: "0.8rem",
            fontWeight: 600,
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {getOpponentName(match, currentTeam)}
        </Typography>
      </Tooltip>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Box
        sx={{ minWidth: "32px", display: "flex", justifyContent: "flex-start" }}
      >
        <Box
          sx={{
            fontSize: "0.6rem",
            px: 0.5,
            py: 0.1,
            bgcolor: "#2D543A",
            borderRadius: 0.5,
            color: "#FFF",
            fontWeight: 600,
          }}
        >
          {currentTeam?.name === match.home_name ? "Home" : "Away"}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {match.league_name && (
        <Tooltip
          title={match.league_name}
          arrow
          placement="bottom"
          enterDelay={500}
        >
          <Box
            sx={{
              fontSize: "0.6rem",
              px: 0.5,
              py: 0.1,
              bgcolor: "#FFCC00",
              borderRadius: 0.5,
              color: "#000",
              fontWeight: 600,
              maxWidth: "50%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ml: "auto",
            }}
          >
            {match.league_name}
          </Box>
        </Tooltip>
      )}
    </Box>
  </Box>
);

interface CalendarCellProps {
  date: Date;
  dayMatches: DetailedMatch[];
  currentTeam: TeamInfo | undefined;
  isCurrentDay: boolean;
  onMatchClick: (match: DetailedMatch) => void;
  formatTimeInTimezone: (date: Date, formatStr: string) => string;
}
const DesktopCalendarCell: React.FC<CalendarCellProps> = ({
  date,
  dayMatches,
  currentTeam,
  isCurrentDay,
  onMatchClick,
  formatTimeInTimezone,
}) => {
  const results = calculateMatchResults(dayMatches, currentTeam);
  return (
    <CalendarCellContainer isCurrentDay={isCurrentDay}>
      <CalendarDayHeader
        date={date}
        isCurrentDay={isCurrentDay}
        results={results}
      />
      {dayMatches.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            overflowY: "auto",
            maxHeight: "calc(100% - 35px)",
            pr: 0.5,
            ...SCROLLBAR_STYLE,
          }}
        >
          {dayMatches
            .slice()
            .sort(
              (a, b) =>
                new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
            )
            .map((match) => (
              <DesktopMatchEntry
                key={match.id}
                match={match}
                currentTeam={currentTeam}
                onMatchClick={onMatchClick}
                formatTimeInTimezone={formatTimeInTimezone}
              />
            ))}
        </Box>
      )}
    </CalendarCellContainer>
  );
};

interface CalendarGridProps {
  matchesByDate: Record<string, DetailedMatch[]>;
  currentTeam: TeamInfo | undefined;
  onMatchClick: (match: DetailedMatch) => void;
  formatTimeInTimezone: (date: Date, formatStr: string) => string;
}
// Monday-first calendar grid layout with proper week boundary handling
const DesktopCalendarGrid: React.FC<
  CalendarGridProps & { currentDate: Date }
> = ({ currentDate, matchesByDate, ...rest }) => {
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const startDay = (getDay(startOfMonth(currentDate)) + 6) % 7;
  const daysInMonth = getDaysInMonth(currentDate);
  const cells = [];

  daysOfWeek.forEach((day) =>
    cells.push(
      <Box
        key={`header-${day}`}
        sx={{
          p: 1.5,
          fontWeight: "bold",
          textAlign: "center",
          borderBottom: `2px solid ${alpha(
            appTheme.palette.primary.main,
            0.2
          )}`,
          backgroundColor: alpha(appTheme.palette.primary.main, 0.05),
          backdropFilter: "blur(5px)",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: alpha(appTheme.palette.primary.main, 0.8),
          }}
        >
          {day}
        </Typography>
      </Box>
    )
  );
  for (let i = 0; i < startDay; i++) {
    cells.push(
      <Paper
        key={`empty-start-${i}`}
        elevation={0}
        sx={{ ...EMPTY_CELL_STYLE, height: 120 }}
      />
    );
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    cells.push(
      <DesktopCalendarCell
        key={`day-${day}`}
        date={date}
        dayMatches={matchesByDate[format(date, "yyyy-MM-dd")] || []}
        isCurrentDay={isToday(date)}
        {...rest}
      />
    );
  }
  const remainingCells = 7 - ((startDay + daysInMonth) % 7);
  if (remainingCells < 7) {
    for (let i = 0; i < remainingCells; i++) {
      cells.push(
        <Paper
          key={`empty-end-${i}`}
          elevation={0}
          sx={{ ...EMPTY_CELL_STYLE, height: 120 }}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gridTemplateRows: "auto repeat(6, minmax(140px, auto))",
        borderRadius: 2,
        border: `1px solid ${alpha(appTheme.palette.primary.main, 0.15)}`,
        backgroundColor: alpha("#000", 0.3),
        boxShadow: `0 8px 16px ${alpha("#000", 0.4)}`,
        gap: 0,
      }}
    >
      {cells}
    </Box>
  );
};

const MobileMatchListItem: React.FC<MatchEntryProps & { date: Date }> = ({
  match,
  currentTeam,
  onMatchClick,
  formatTimeInTimezone,
}) => {
  const opponent = getOpponentName(match, currentTeam);
  const statusColor = getMatchStatusColor(match.status);

  return (
    <Box
      onClick={() => onMatchClick(match)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        mb: 1,
        backgroundColor: alpha(statusColor, 0.1),
        border: `1px solid ${alpha(statusColor, 0.3)}`,
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: alpha(statusColor, 0.2),
          transform: "scale(1.02)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          minWidth: 80,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: alpha("#FFFFFF", 0.7),
            lineHeight: 1,
          }}
        >
          {format(new Date(match.datetime), "MMM d")}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: appTheme.palette.primary.main,
            lineHeight: 1,
          }}
        >
          {formatTimeInTimezone(new Date(match.datetime), "HH:mm")}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, textAlign: "center", px: 1 }}>
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#FFFFFF",
            lineHeight: 1.2,
          }}
        >
          vs {opponent}
        </Typography>
        {currentTeam && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: alpha("#FFFFFF", 0.6),
              lineHeight: 1,
            }}
          >
            {currentTeam.name === match.home_name ? "Home" : "Away"}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          minWidth: 60,
        }}
      >
        {match.status === MatchStatus.Complete && currentTeam ? (
          <>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 700,
                color: isWin(match, currentTeam)
                  ? appTheme.palette.success.main
                  : isDraw(match, currentTeam)
                  ? appTheme.palette.warning.main
                  : appTheme.palette.error.main,
                lineHeight: 1,
              }}
            >
              {currentTeam.name === match.home_name
                ? `${match.home_score}-${match.away_score}`
                : `${match.away_score}-${match.home_score}`}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: isWin(match, currentTeam)
                  ? appTheme.palette.success.main
                  : isDraw(match, currentTeam)
                  ? appTheme.palette.warning.main
                  : appTheme.palette.error.main,
                lineHeight: 1,
              }}
            >
              {isWin(match, currentTeam)
                ? "WIN"
                : isDraw(match, currentTeam)
                ? "DRAW"
                : "LOSS"}
            </Typography>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: statusColor,
              fontWeight: 600,
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            {match.status === MatchStatus.Scheduled
              ? "Upcoming"
              : match.status === MatchStatus.InProgress
              ? "Live"
              : match.status === MatchStatus.Disputed
              ? "Disputed"
              : "TBD"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// List view optimized for mobile: chronological order with clear visual hierarchy
const MobileMatchList: React.FC<CalendarGridProps & { currentDate: Date }> = ({
  matchesByDate,
  currentTeam,
  onMatchClick,
  formatTimeInTimezone,
}) => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);

  const allMatchesInMonth = Object.entries(matchesByDate)
    .map(([dateStr, matches]) => ({
      date: new Date(dateStr),
      dateStr,
      matches: matches.sort(
        (a, b) =>
          new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      ),
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  // Auto-scroll to today's matches or next upcoming match
  useEffect(() => {
    if (!listContainerRef.current || allMatchesInMonth.length === 0) return;

    const container = listContainerRef.current;
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");

    // Find today's matches first
    const todayIndex = allMatchesInMonth.findIndex(
      ({ dateStr }) => dateStr === todayStr
    );

    let targetIndex = todayIndex;

    // If no matches today, find the next upcoming match
    if (todayIndex === -1) {
      targetIndex = allMatchesInMonth.findIndex(({ date }) => date >= today);
      // If no upcoming matches, scroll to the first match
      if (targetIndex === -1) {
        targetIndex = 0;
      }
    }

    const timeoutId = setTimeout(() => {
      if (targetIndex >= 0 && targetIndex < allMatchesInMonth.length) {
        const targetElement = container.children[targetIndex] as HTMLElement;
        if (targetElement) {
          // Calculate the scroll position to put the target at the top with some padding
          const containerTop = container.offsetTop;
          const targetTop = targetElement.offsetTop;
          const scrollPosition = targetTop - containerTop - 16;

          container.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: "smooth",
          });
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [allMatchesInMonth]);

  if (allMatchesInMonth.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" sx={{ color: alpha("#FFFFFF", 0.8), mb: 1 }}>
          No matches this month
        </Typography>
        <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.6) }}>
          Check other months or adjust your filters
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={listContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxHeight: "calc(100vh - 300px)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        ...SCROLLBAR_STYLE,
      }}
    >
      {allMatchesInMonth.map(({ date, matches }) => {
        const isToday =
          format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

        return (
          <Box
            key={format(date, "yyyy-MM-dd")}
            ref={isToday ? todayRef : undefined}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                mt: 2,
                "&:first-of-type": { mt: 0 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: isToday
                    ? appTheme.palette.primary.main
                    : alpha(appTheme.palette.primary.main, 0.3),
                  color: isToday ? "#000" : "#FFF",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.8rem",
                }}
              >
                {isToday ? "TODAY" : format(date, "EEEE, MMM d")}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: alpha(appTheme.palette.primary.main, 0.2),
                  ml: 2,
                }}
              />
            </Box>

            {matches.map((match) => (
              <MobileMatchListItem
                key={match.id}
                match={match}
                currentTeam={currentTeam}
                onMatchClick={onMatchClick}
                formatTimeInTimezone={formatTimeInTimezone}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

const MobileCalendarGrid: React.FC<
  CalendarGridProps & {
    currentDate: Date;
    onDayClick: (date: Date, matches: DetailedMatch[]) => void;
  }
> = ({ currentDate, matchesByDate, onDayClick }) => {
  const daysOfWeek = ["M", "T", "W", "T", "F", "S", "S"];
  const startDay = (getDay(startOfMonth(currentDate)) + 6) % 7;
  const daysInMonth = getDaysInMonth(currentDate);
  const cells = [];

  daysOfWeek.forEach((day, index) =>
    cells.push(
      <Box
        key={`header-${day}-${index}`}
        sx={{
          p: 1,
          fontWeight: "bold",
          textAlign: "center",
          backgroundColor: alpha(appTheme.palette.primary.main, 0.1),
          borderBottom: `1px solid ${alpha(
            appTheme.palette.primary.main,
            0.3
          )}`,
          fontSize: "0.7rem",
        }}
      >
        {day}
      </Box>
    )
  );

  for (let i = 0; i < startDay; i++) {
    cells.push(
      <Box
        key={`empty-start-${i}`}
        sx={{
          backgroundColor: alpha(appTheme.palette.background.paper, 0.03),
          p: 0.5,
          opacity: 0.3,
          minHeight: 40,
        }}
      />
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    const dayMatches = matchesByDate[format(date, "yyyy-MM-dd")] || [];
    const hasMatches = dayMatches.length > 0;

    cells.push(
      <Box
        key={`day-${day}`}
        onClick={() => hasMatches && onDayClick(date, dayMatches)}
        sx={{
          backgroundColor: isToday(date)
            ? alpha(appTheme.palette.primary.main, 0.2)
            : alpha("#2A2A2A", 0.5),
          border: `1px solid ${alpha(
            appTheme.palette.primary.main,
            isToday(date) ? 0.5 : 0.1
          )}`,
          borderRadius: 1,
          p: 0.5,
          minHeight: 40,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          cursor: hasMatches ? "pointer" : "default",
          "&:hover": hasMatches
            ? {
                backgroundColor: alpha(appTheme.palette.primary.main, 0.1),
              }
            : {},
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: isToday(date) ? 700 : 500,
            color: isToday(date) ? appTheme.palette.primary.main : "#FFF",
          }}
        >
          {day}
        </Typography>
        {hasMatches && (
          <Box
            sx={{
              width: 4,
              height: 4,
              backgroundColor: appTheme.palette.primary.main,
              borderRadius: "50%",
              mt: 0.25,
            }}
          />
        )}
      </Box>
    );
  }

  const remainingCells = 7 - ((startDay + daysInMonth) % 7);
  if (remainingCells < 7) {
    for (let i = 0; i < remainingCells; i++) {
      cells.push(
        <Box
          key={`empty-end-${i}`}
          sx={{
            backgroundColor: alpha(appTheme.palette.background.paper, 0.03),
            p: 0.5,
            opacity: 0.3,
            minHeight: 40,
          }}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 0.5,
        borderRadius: 2,
        border: `1px solid ${alpha(appTheme.palette.primary.main, 0.2)}`,
        backgroundColor: alpha("#000", 0.3),
        p: 0.5,
      }}
    >
      {cells}
    </Box>
  );
};

interface DayMatchesModalProps {
  open: boolean;
  onClose: () => void;
  date: Date | null;
  matches: DetailedMatch[];
  currentTeam: TeamInfo | undefined;
  onMatchClick: (match: DetailedMatch) => void;
  formatTimeInTimezone: (date: Date, formatStr: string) => string;
}

const DayMatchesModal: React.FC<DayMatchesModalProps> = ({
  open,
  onClose,
  date,
  matches,
  currentTeam,
  onMatchClick,
  formatTimeInTimezone,
}) => {
  if (!date) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        p: 0,
      }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          sx={{
            width: "100vw",
            maxWidth: "100vw",
            height: "auto",
            minHeight: 0,
            maxHeight: "100dvh",
            borderRadius: "16px 16px 0 0",
            backgroundColor: alpha("#1A1A1A", 0.98),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(appTheme.palette.primary.main, 0.2)}`,
            boxShadow: `0 -4px 20px ${alpha("#000", 0.5)}`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: `1px solid ${alpha(
                appTheme.palette.primary.main,
                0.15
              )}`,
              backgroundColor: alpha(appTheme.palette.primary.main, 0.05),
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: appTheme.palette.primary.main,
                  lineHeight: 1.2,
                }}
              >
                {isToday(date) ? "Today" : format(date, "EEEE")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha("#FFFFFF", 0.7),
                  lineHeight: 1,
                }}
              >
                {format(date, "MMMM d, yyyy")}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: alpha("#FFFFFF", 0.7),
                "&:hover": {
                  backgroundColor: alpha(appTheme.palette.primary.main, 0.1),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Matches List */}
          <Box
            sx={{
              flex: "1 1 auto",
              overflowY: "auto",
              p: 2,
              ...SCROLLBAR_STYLE,

              minHeight: matches.length === 0 ? 0 : undefined,
            }}
          >
            {matches.length === 0 ? (
              <Typography
                sx={{
                  color: alpha("#FFF", 0.7),
                  textAlign: "center",
                  py: 4,
                }}
              >
                No matches for this day.
              </Typography>
            ) : (
              matches
                .slice()
                .sort(
                  (a, b) =>
                    new Date(a.datetime).getTime() -
                    new Date(b.datetime).getTime()
                )
                .map((match) => (
                  <MobileMatchListItem
                    key={match.id}
                    match={match}
                    currentTeam={currentTeam}
                    onMatchClick={(match) => {
                      onMatchClick(match);
                      onClose();
                    }}
                    formatTimeInTimezone={formatTimeInTimezone}
                  />
                ))
            )}
          </Box>
        </Paper>
      </Slide>
    </Modal>
  );
};
export const StandaloneCalendar: React.FC = () => {
  const history = useHistory();
  const theme = useTheme();
  // noSsr: under React 17 useMediaQuery returns `false` on the first render and
  // only corrects itself in a layout effect. Without it the desktop leaderboard
  // mounts for one commit on mobile, and AdSenseUnit's effect fires a real ad
  // request at phone width for a slot that is destroyed before it can be seen.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { data: userTeams, isLoading: userTeamsLoading } = useUserMyTeams();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedTeamSlug, setSelectedTeamSlug] = useState<string | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>("all");
  const [selectedResultFilter, setSelectedResultFilter] = useState<
    "all" | "wins" | "draws" | "losses"
  >("all");
  const browserTimezone =
    typeof window !== "undefined" && Intl?.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "Europe/London";
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    browserTimezone || "Europe/London"
  );
  const [mobileView, setMobileView] = useState<"calendar" | "list">("list");

  const [dayMatchesModalOpen, setDayMatchesModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayMatches, setSelectedDayMatches] = useState<DetailedMatch[]>(
    []
  );

  const [teamSelectorAnchor, setTeamSelectorAnchor] =
    useState<null | HTMLElement>(null);
  const [leagueSelectorAnchor, setLeagueSelectorAnchor] =
    useState<null | HTMLElement>(null);
  const [resultSelectorAnchor, setResultSelectorAnchor] =
    useState<null | HTMLElement>(null);
  const [timezoneSelectorAnchor, setTimezoneSelectorAnchor] =
    useState<null | HTMLElement>(null);

  const {
    currentDate,
    goToPrevMonth,
    goToNextMonth,
    goToCurrentMonth,
    prevMonthName,
    nextMonthName,
  } = useCalendarNavigation();
  const { allMatches: rawMatches, isLoading: matchesLoading } =
    useCombinedTeamMatches(selectedTeamSlug);

  // Auto-select first team to prevent empty state on initial load
  useEffect(() => {
    if (!userTeamsLoading && userTeams && userTeams.length > 0) {
      setSelectedTeamSlug(userTeams[0].team.slug);
    }
  }, [userTeams, userTeamsLoading]);
  useEffect(() => {
    setSelectedLeagueId("all");
  }, [selectedTeamSlug]);

  const availableTeams = useMemo(
    () =>
      userTeams?.map(({ team }) => ({
        id: team.id,
        name: team.name,
        slug: team.slug,
        logo: team.logo,
      })) || [],
    [userTeams]
  );
  const currentTeam = useMemo(
    () => availableTeams.find((team) => team.slug === selectedTeamSlug),
    [availableTeams, selectedTeamSlug]
  );
  const currentTimezone = useMemo(
    () => TIMEZONE_OPTIONS.find((tz) => tz.id === selectedTimezone),
    [selectedTimezone]
  );
  const availableLeagues = useMemo(
    () =>
      uniqBy(
        rawMatches
          .filter((m) => m.league_id && m.league_name)
          .map((m) => ({ id: m.league_id, name: m.league_name })),
        "id"
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [rawMatches]
  );
  const currentLeague = useMemo(
    () =>
      selectedLeagueId === "all"
        ? { id: "all", name: "All Leagues" }
        : availableLeagues.find((l) => l.id === selectedLeagueId),
    [availableLeagues, selectedLeagueId]
  );

  const formatTimeInTimezone = useMemo(
    () => (date: Date, formatStr: string) =>
      formatInTimeZone(date, selectedTimezone, formatStr),
    [selectedTimezone]
  );
  const getMatchTimeInTimezone = useMemo(
    () => (utcDatetime: string) =>
      utcToZonedTime(new Date(utcDatetime), selectedTimezone),
    [selectedTimezone]
  );

  // Filter cascade: league → month → result type, with timezone conversion for date grouping
  const filteredMatches = useMemo(() => {
    const leagueFiltered =
      selectedLeagueId === "all" || !selectedLeagueId
        ? rawMatches
        : rawMatches.filter((m) => m.league_id === selectedLeagueId);
    const monthFiltered = leagueFiltered.filter((m) =>
      isSameMonth(new Date(m.datetime), currentDate)
    );
    if (!currentTeam) return monthFiltered;
    switch (selectedResultFilter) {
      case "wins":
        return monthFiltered.filter((m) => isWin(m, currentTeam));
      case "draws":
        return monthFiltered.filter(isDraw);
      case "losses":
        return monthFiltered.filter((m) => isLoss(m, currentTeam));
      default:
        return monthFiltered;
    }
  }, [
    rawMatches,
    currentDate,
    selectedLeagueId,
    selectedResultFilter,
    currentTeam,
  ]);

  const matchesByDate = useMemo(
    () =>
      groupBy(filteredMatches, (m) =>
        format(getMatchTimeInTimezone(m.datetime), "yyyy-MM-dd")
      ),
    [filteredMatches, getMatchTimeInTimezone]
  );

  const handleCloseMenus = () => {
    setTeamSelectorAnchor(null);
    setLeagueSelectorAnchor(null);
    setResultSelectorAnchor(null);
    setTimezoneSelectorAnchor(null);
  };
  const handleTeamSelect = (slug: string) => {
    handleCloseMenus();
    setSelectedTeamSlug(slug);
  };
  const handleLeagueSelect = (id: string) => {
    handleCloseMenus();
    setSelectedLeagueId(id);
  };
  const handleResultSelect = (filter: "all" | "wins" | "draws" | "losses") => {
    handleCloseMenus();
    setSelectedResultFilter(filter);
  };
  const handleTimezoneSelect = (id: string) => {
    handleCloseMenus();
    setSelectedTimezone(id);
  };
  const handleMatchClick = (match: DetailedMatch) =>
    history.push(`/match/${match.id}`);

  const handleDayClick = (date: Date, matches: DetailedMatch[]) => {
    setSelectedDate(date);
    setSelectedDayMatches(matches);
    setDayMatchesModalOpen(true);
  };

  const handleCloseDayModal = () => {
    setDayMatchesModalOpen(false);
    setSelectedDate(null);
    setSelectedDayMatches([]);
  };

  const isLoading = userTeamsLoading || matchesLoading;
  const hasNoTeams = !isLoading && isEmpty(availableTeams);
  const hasNoMatches = !isLoading && currentTeam && isEmpty(filteredMatches);

  const resultFilterLabels = {
    all: "All Results",
    wins: "Wins",
    draws: "Draws",
    losses: "Losses",
  };

  return (
    <ThemeProvider theme={appTheme}>
      {!isMobile && (
        <div
          style={{
            background: "linear-gradient(180deg, #000 0%, #0b0b0b 100%)",
            borderBottom: "1px solid #111",
            padding: "6px 0 8px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SafeStickyAd
            placementName="billboard"
            adSenseUnit="desktop_top_leaderboard"
            debounceMs={690}
            minHeight="90px"
          />
        </div>
      )}
      {isMobile && (
        <Box
          sx={{
            backgroundColor: "#121212",
            textAlign: "center",
            py: 1,
            borderBottom: "1px solid #222",
          }}
        >
          <SafeStickyAd
            placementName="mobile_banner"
            adSenseUnit="mobile_inline_banner"
            debounceMs={490}
          />
        </Box>
      )}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0E0E0E 0%, #1E1E1E 100%)",
          py: { xs: 2, md: 3 },
          minHeight: "100vh",
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 } }}>
          <Boundary isLoading={isLoading} centerStatus>
            {hasNoTeams ? (
              <NoTeamsMessage />
            ) : (
              <>
                <Paper
                  sx={{
                    ...ENHANCED_CARD_STYLE,
                    minHeight: "calc(100vh - 120px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ p: { xs: 2, md: 3 }, flexShrink: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        rowGap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{ fontWeight: 700, letterSpacing: "0.02em" }}
                          >
                            {format(currentDate, "MMMM yyyy")}
                          </Typography>
                          <TeamSelector
                            teams={availableTeams}
                            selectedTeamSlug={selectedTeamSlug}
                            onTeamSelect={handleTeamSelect}
                            isOpen={!!teamSelectorAnchor}
                            onToggle={(e) =>
                              setTeamSelectorAnchor(e.currentTarget)
                            }
                            isMobile={isMobile}
                          />
                          {availableLeagues.length > 0 && (
                            <SelectorControl
                              isOpen={!!leagueSelectorAnchor}
                              onClick={(e) =>
                                setLeagueSelectorAnchor(e.currentTarget)
                              }
                              ariaControls="league-menu"
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                                }}
                              >
                                {currentLeague?.name || "Select League"}
                              </Typography>
                            </SelectorControl>
                          )}
                          <SelectorControl
                            isOpen={!!resultSelectorAnchor}
                            onClick={(e) =>
                              setResultSelectorAnchor(e.currentTarget)
                            }
                            ariaControls="result-menu"
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: isMobile ? "0.8rem" : "0.9rem",
                              }}
                            >
                              {resultFilterLabels[selectedResultFilter]}
                            </Typography>
                          </SelectorControl>
                        </Box>
                        {!isMobile && <MatchStatusLegend />}
                        {isMobile && (
                          <MobileViewToggle
                            view={mobileView}
                            onViewChange={setMobileView}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "center",
                          flexWrap: "wrap",
                          alignSelf: "flex-start",
                        }}
                      >
                        <SelectorControl
                          isOpen={!!timezoneSelectorAnchor}
                          onClick={(e) =>
                            setTimezoneSelectorAnchor(e.currentTarget)
                          }
                          ariaControls="timezone-menu"
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: alpha("#FFFFFF", 0.7),
                              fontSize: isMobile ? "0.7rem" : "0.8rem",
                              mr: 0.5,
                            }}
                          >
                            Time:
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: isMobile ? "0.75rem" : "0.85rem",
                            }}
                          >
                            {currentTimezone?.name || "UK"}
                          </Typography>
                        </SelectorControl>
                        <Button
                          variant="outlined"
                          startIcon={
                            !isMobile && (
                              <ArrowBackIosNewIcon sx={{ fontSize: "1rem" }} />
                            )
                          }
                          onClick={goToPrevMonth}
                          size="small"
                        >
                          {!isMobile ? prevMonthName : "<"}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={!isMobile && <TodayIcon />}
                          onClick={goToCurrentMonth}
                          size="small"
                        >
                          {isMobile ? "Today" : "Today"}
                        </Button>
                        <Button
                          variant="outlined"
                          endIcon={
                            !isMobile && (
                              <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
                            )
                          }
                          onClick={goToNextMonth}
                          size="small"
                        >
                          {!isMobile ? nextMonthName : ">"}
                        </Button>
                      </Box>
                    </Box>
                    {isMobile && (
                      <Box sx={{ mt: 2 }}>
                        <MatchStatusLegend />
                      </Box>
                    )}
                  </Box>

                  {hasNoMatches ? (
                    <NoMatchesMessage
                      leagueName={
                        selectedLeagueId !== "all"
                          ? currentLeague?.name
                          : undefined
                      }
                      month={format(currentDate, "MMMM yyyy")}
                    />
                  ) : (
                    <Box
                      sx={{
                        px: { xs: 2, md: 3 },
                        pb: { xs: 2, md: 3 },
                        display: "flex",
                        flex: 1,
                        flexDirection: "column",
                      }}
                    >
                      {isMobile ? (
                        mobileView === "list" ? (
                          <MobileMatchList
                            currentDate={currentDate}
                            matchesByDate={matchesByDate}
                            currentTeam={currentTeam}
                            onMatchClick={handleMatchClick}
                            formatTimeInTimezone={formatTimeInTimezone}
                          />
                        ) : (
                          <MobileCalendarGrid
                            currentDate={currentDate}
                            matchesByDate={matchesByDate}
                            currentTeam={currentTeam}
                            onMatchClick={handleMatchClick}
                            formatTimeInTimezone={formatTimeInTimezone}
                            onDayClick={handleDayClick}
                          />
                        )
                      ) : (
                        <DesktopCalendarGrid
                          currentDate={currentDate}
                          matchesByDate={matchesByDate}
                          currentTeam={currentTeam}
                          onMatchClick={handleMatchClick}
                          formatTimeInTimezone={formatTimeInTimezone}
                        />
                      )}
                    </Box>
                  )}

                  {/* Menus for team, league, result filter, and timezone selection */}
                  <StyledMenu
                    id="team-menu"
                    anchorEl={teamSelectorAnchor}
                    open={!!teamSelectorAnchor}
                    onClose={handleCloseMenus}
                  >
                    {availableTeams.map((team) => (
                      <StyledMenuItem
                        key={team.slug}
                        onClick={() => handleTeamSelect(team.slug)}
                        selected={team.slug === selectedTeamSlug}
                      >
                        <Box
                          sx={{
                            ...TEAM_LOGO_CONTAINER_STYLE,
                            width: 24,
                            height: 24,
                            mr: 1,
                          }}
                        >
                          <CDNImage
                            imageId={team.logo}
                            variant={ImageVariant.SmallThumbnail}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight:
                              team.slug === selectedTeamSlug ? 700 : 500,
                          }}
                        >
                          {team.name}
                        </Typography>
                      </StyledMenuItem>
                    ))}
                  </StyledMenu>
                  <StyledMenu
                    id="league-menu"
                    anchorEl={leagueSelectorAnchor}
                    open={!!leagueSelectorAnchor}
                    onClose={handleCloseMenus}
                  >
                    <StyledMenuItem
                      onClick={() => handleLeagueSelect("all")}
                      selected={selectedLeagueId === "all"}
                    >
                      <Typography
                        sx={{
                          fontWeight: selectedLeagueId === "all" ? 700 : 500,
                        }}
                      >
                        All Leagues
                      </Typography>
                    </StyledMenuItem>
                    {availableLeagues.map((league) => (
                      <StyledMenuItem
                        key={league.id}
                        onClick={() => handleLeagueSelect(league.id)}
                        selected={league.id === selectedLeagueId}
                      >
                        <Typography
                          sx={{
                            fontWeight:
                              league.id === selectedLeagueId ? 700 : 500,
                          }}
                        >
                          {league.name}
                        </Typography>
                      </StyledMenuItem>
                    ))}
                  </StyledMenu>
                  <StyledMenu
                    id="result-menu"
                    anchorEl={resultSelectorAnchor}
                    open={!!resultSelectorAnchor}
                    onClose={handleCloseMenus}
                    minWidth={180}
                  >
                    {Object.entries(resultFilterLabels).map(([key, label]) => (
                      <StyledMenuItem
                        key={key}
                        onClick={() =>
                          handleResultSelect(
                            key as "all" | "wins" | "draws" | "losses"
                          )
                        }
                        selected={key === selectedResultFilter}
                      >
                        <Typography
                          sx={{
                            fontWeight:
                              key === selectedResultFilter ? 700 : 500,
                          }}
                        >
                          {label}
                        </Typography>
                      </StyledMenuItem>
                    ))}
                  </StyledMenu>
                  <StyledMenu
                    id="timezone-menu"
                    anchorEl={timezoneSelectorAnchor}
                    open={!!timezoneSelectorAnchor}
                    onClose={handleCloseMenus}
                    minWidth={280}
                    maxHeight={400}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom: `1px solid ${alpha(
                          appTheme.palette.primary.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: appTheme.palette.primary.main,
                          fontSize: "0.9rem",
                        }}
                      >
                        Select Time Zone
                      </Typography>
                    </Box>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <StyledMenuItem
                        key={tz.id}
                        onClick={() => handleTimezoneSelect(tz.id)}
                        selected={tz.id === selectedTimezone}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight:
                                tz.id === selectedTimezone ? 700 : 500,
                              fontSize: "0.9rem",
                            }}
                          >
                            {tz.name}{" "}
                            <span style={{ color: "#888", fontSize: "0.8em" }}>
                              ({tz.offset})
                            </span>
                          </Typography>
                        </Box>
                      </StyledMenuItem>
                    ))}
                  </StyledMenu>
                </Paper>
              </>
            )}
          </Boundary>
        </Container>
        {isMobile && (
          <DayMatchesModal
            open={dayMatchesModalOpen}
            onClose={handleCloseDayModal}
            date={selectedDate}
            matches={selectedDayMatches}
            currentTeam={currentTeam}
            onMatchClick={handleMatchClick}
            formatTimeInTimezone={formatTimeInTimezone}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

interface MobileViewToggleProps {
  view: "calendar" | "list";
  onViewChange: (view: "calendar" | "list") => void;
}

const MobileViewToggle: React.FC<MobileViewToggleProps> = ({
  view,
  onViewChange,
}) => (
  <Box sx={{ display: "flex", gap: 1 }}>
    <Button
      variant={view === "list" ? "contained" : "outlined"}
      size="small"
      onClick={() => onViewChange("list")}
    >
      List
    </Button>
    <Button
      variant={view === "calendar" ? "contained" : "outlined"}
      size="small"
      onClick={() => onViewChange("calendar")}
    >
      Calendar
    </Button>
  </Box>
);

const NoMatchesMessage: React.FC<{ leagueName?: string; month?: string }> = ({
  leagueName,
  month,
}) => (
  <Box sx={{ textAlign: "center", py: 8 }}>
    <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
      No matches found
    </Typography>
    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
      {leagueName && (
        <>
          for <b>{leagueName}</b>
        </>
      )}
      {month && (
        <>
          {" "}
          in <b>{month}</b>
        </>
      )}
      .
    </Typography>
  </Box>
);

const NoTeamsMessage: React.FC = () => (
  <Box sx={{ textAlign: "center", py: 8 }}>
    <Typography variant="h6" sx={{ color: "#fff", mb: 1 }}>
      No teams found
    </Typography>
    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
      You are not a member of any teams yet.
    </Typography>
  </Box>
);

// Match rendering components
const MatchScore: React.FC<{
  match: DetailedMatch;
  currentTeam: TeamInfo | undefined;
}> = ({ match, currentTeam }) => {
  if (match.status !== MatchStatus.Complete || !currentTeam) return null;

  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color: isWin(match, currentTeam)
          ? appTheme.palette.success.main
          : isDraw(match)
          ? appTheme.palette.warning.main
          : "#FFF",
      }}
    >
      {currentTeam.name === match.home_name
        ? `${match.home_score}-${match.away_score}`
        : `${match.away_score}-${match.home_score}`}
    </Typography>
  );
};

const MatchTime: React.FC<{
  datetime: string;
  formatTimeInTimezone: (date: Date, formatStr: string) => string;
}> = ({ datetime, formatTimeInTimezone }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: appTheme.palette.primary.main,
      fontSize: "0.7rem",
      minWidth: "32px",
    }}
  >
    {formatTimeInTimezone(new Date(datetime), "HH:mm")}
  </Typography>
);

const TeamSelector: React.FC<{
  teams: Array<{ slug: string; name: string; logo: string }>;
  selectedTeamSlug: string | null;
  isOpen: boolean;
  onToggle: (event: React.MouseEvent<HTMLElement>) => void;
  isMobile: boolean;
}> = ({ teams, selectedTeamSlug, isOpen, onToggle, isMobile }) => {
  const currentTeam = teams.find((team) => team.slug === selectedTeamSlug);

  return (
    <>
      <SelectorControl
        isOpen={isOpen}
        onClick={onToggle}
        ariaControls="team-menu"
      >
        <Box
          sx={{
            ...TEAM_LOGO_CONTAINER_STYLE,
            width: isMobile ? 24 : 32,
            height: isMobile ? 24 : 32,
          }}
        >
          {currentTeam && (
            <CDNImage
              imageId={currentTeam.logo}
              variant={ImageVariant.SmallThumbnail}
            />
          )}
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "0.8rem" : "1rem",
          }}
        >
          {currentTeam?.name || "Select Team"}
        </Typography>
      </SelectorControl>
    </>
  );
};

const CalendarCellContainer: React.FC<{
  isCurrentDay: boolean;
  children: React.ReactNode;
}> = ({ isCurrentDay, children }) => (
  <Paper
    elevation={0}
    sx={{
      background: isCurrentDay
        ? `linear-gradient(145deg, ${alpha(
            appTheme.palette.primary.main,
            0.15
          )} 0%, ${alpha(appTheme.palette.primary.main, 0.05)} 100%)`
        : `linear-gradient(145deg, ${alpha("#2A2A2A", 0.7)} 0%, ${alpha(
            "#1A1A1A",
            0.7
          )} 100%)`,
      backdropFilter: "blur(10px)",
      border: `1px solid ${alpha(
        appTheme.palette.primary.main,
        isCurrentDay ? 0.5 : 0.1
      )}`,
      height: 140,
      p: 1.5,
      m: 0.5,
      display: "flex",
      flexDirection: "column",
      borderRadius: 2,
      transition: "all 0.3s ease",
      boxShadow: isCurrentDay
        ? `0 0 15px ${alpha(appTheme.palette.primary.main, 0.3)}`
        : "none",
      overflow: "hidden",
      position: "relative",
      "&:hover": {
        boxShadow: `0 5px 15px ${alpha("#000", 0.3)}`,
        border: `1px solid ${alpha(
          appTheme.palette.primary.main,
          isCurrentDay ? 0.6 : 0.3
        )}`,
      },
    }}
  >
    {children}
  </Paper>
);

const CalendarDayHeader: React.FC<{
  date: Date;
  isCurrentDay: boolean;
  results?: { wins: number; draws: number; losses: number; points: number };
}> = ({ date, isCurrentDay, results }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
        minHeight: "24px",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: isCurrentDay ? 700 : 500,
          color: isCurrentDay ? appTheme.palette.primary.main : "text.primary",
        }}
      >
        {format(date, "d")}
      </Typography>
      {results &&
        (results.wins > 0 || results.draws > 0 || results.losses > 0) && (
          <DayResultBadges results={results} />
        )}
    </Box>
  );
};
